import { useFormSubmission } from '@commenda/api/form-builder/form-submissions';
import { useFormTemplateKey } from '@commenda/api/form-builder/form-template-keys';
import { useFormsTemplate } from '@commenda/api/form-builder/forms-templates';
import { useIndirectTaxRegistration } from '@commenda/api/indirect-tax/registrations';
import { IndirectTaxRegistrationsHelper } from '../../../indirect-tax/registrations/helpers';
import { IndirectTaxRegistrationHelper } from '../../../indirect-tax/registrations/utils/IndirectTaxRegistrationHelper';
import type { UKActivity } from '../wrappers/IndirectTaxRegistration/types';
import type { IAddress } from '@commenda/types';
import { IndirectTaxFilingsHelper } from '../../../indirect-tax/filings/helpers';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DocViewerComponent,
  Icons,
  TruncatedText,
} from '@commenda/kakashi';
import { LoadingIndicator } from '@commenda/kakashi/src/components/LoadingIndicator';
import {
  cn,
  copyToClipboard,
  downloadFileFromFileId,
  downloadFileFromLink,
  enumKeyToDisplayName,
  formatIsoDateShort,
} from '@commenda/utils';
import { getSignedUrlByFileId } from '@commenda/utils/files/uploadFile';
import { getSignedUrlForHostedFileUrl } from 'node_modules/@commenda/kakashi';
import { useMemo, useState } from 'react';
import { GridView, TGridViewDataPoint } from '../../../components/GridView';
import { PERSON_LABELS_KEY } from '../../dsl-constants';
import { DataPointButton } from '../../../Corporation/common/DataPoint/DataPointButton';
import { useSubmissionParser } from '../../../hooks/form-builder/useSubmissionParser';
import { useNotification } from '../../../hooks/useNotification';
import { formatNumberForDisplay } from '../../utils/number-display';
import {
  readEntityDetailsKeyId,
  readPartnershipStructure,
} from '../wrappers/Ownership/copy';
import {
  formatOwnershipValue,
  isOwnershipValue,
} from '../wrappers/Ownership/format';
import { OwnershipSummaryHelper } from '../wrappers/Ownership/OwnershipSummaryHelper';
import { OwnershipSummaryView } from '../wrappers/Ownership/OwnershipSummaryView';

interface ReviewProps {
  submissionId: string;
  className?: string;
  corporationId?: number;
  showCopyButton?: boolean;
  /** Step headings matching this title are omitted, to avoid repeating the page heading */
  pageTitle?: string;
}

const isSameTitle = (a: string, b?: string): boolean =>
  !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

// Helper to detect if an object is a FileUpload
const isFileUpload = (value: unknown): boolean => {
  if (
    typeof value !== 'object' ||
    value === null ||
    Array.isArray(value) ||
    value instanceof File
  ) {
    return false;
  }
  const obj = value as Record<string, unknown>;
  const fileKeys = ['fileName', 'fileUrl', 'documentType'];
  return fileKeys.some((key) => key in obj);
};

// Helper to detect if a value is an array of FileBasic objects (MultiFileUpload)
const isMultiFileUpload = (value: unknown): boolean => {
  if (!Array.isArray(value) || value.length === 0) {
    return false;
  }
  // Check if all items in the array are FileBasic objects
  return value.every((item) => {
    if (typeof item !== 'object' || item === null || item instanceof File) {
      return false;
    }
    const obj = item as Record<string, unknown>;
    const fileKeys = ['fileName', 'fileUrl', 'documentType'];
    return fileKeys.some((key) => key in obj);
  });
};

// Fallback label formatter
const formatFieldId = (fieldId: string): string => {
  return fieldId
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]/g, ' ')
    .replace(/^\s/, '')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
    .trim();
};

// Helper to detect if a value is a DynamicFields data object
const isDynamicFieldsData = (
  value: unknown
): value is Record<string, unknown> => {
  if (
    typeof value !== 'object' ||
    value === null ||
    Array.isArray(value) ||
    value instanceof File
  ) {
    return false;
  }
  // DynamicFields stores data as a flat object with field keys
  // It should not have special keys like __meta, fileName, fileUrl, addressLine1, etc.
  const obj = value as Record<string, unknown>;
  const fileKeys = ['fileName', 'fileUrl', 'documentType'];
  const addressKeys = [
    'addressLine1',
    'addressLine2',
    'postalCode',
    'city',
    'state',
    'country',
  ];
  const metaKeys = ['__meta'];

  const hasFileKeys = fileKeys.some((key) => key in obj);
  const hasAddressKeys = addressKeys.some((key) => key in obj);
  const hasMetaKeys = metaKeys.some((key) => key in obj);

  // If it has file, address, or meta keys, it's not DynamicFields data
  if (hasFileKeys || hasAddressKeys || hasMetaKeys) {
    return false;
  }

  // It should have at least one key and all values should be primitives or arrays of primitives
  const keys = Object.keys(obj);
  if (keys.length === 0) return false;

  return true;
};

// Format a single value from DynamicFields based on field definition
const formatDynamicFieldValue = (
  value: unknown,
  fieldDef?: {
    type?: string;
    options?: Array<{ value: string; label: string }>;
  }
): string => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  // Handle select type with options - look up the label
  if (
    fieldDef?.options &&
    Array.isArray(fieldDef.options) &&
    fieldDef.options.length > 0
  ) {
    // Handle array values (multi-select)
    if (Array.isArray(value)) {
      const labels = value.map((v) => {
        const option = fieldDef.options!.find(
          (opt) => String(opt.value) === String(v)
        );
        return option?.label || String(v);
      });
      return labels.length > 0 ? labels.join(', ') : '—';
    }
    // Handle single value
    const option = fieldDef.options.find(
      (opt) => String(opt.value) === String(value)
    );
    if (option?.label) {
      return option.label;
    }
  }

  if (fieldDef?.type === 'number') {
    const formatted = formatNumberForDisplay(value);
    if (formatted !== null) return formatted;
  }

  // Handle arrays
  if (Array.isArray(value)) {
    return value.length > 0 ? value.map((v) => String(v)).join(', ') : '—';
  }

  // Handle booleans
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (
    typeof value === 'object' &&
    value !== null &&
    isOwnershipValue(value as Record<string, unknown>)
  ) {
    return formatOwnershipValue(value);
  }

  return String(value);
};

// Helper to detect if an object is an AddressInput
const isAddressInput = (value: unknown): boolean => {
  if (
    typeof value !== 'object' ||
    value === null ||
    Array.isArray(value) ||
    value instanceof File
  ) {
    return false;
  }
  const obj = value as Record<string, unknown>;
  const addressKeys = [
    'addressLine1',
    'addressLine2',
    'postalCode',
    'city',
    'state',
    'country',
  ];
  return addressKeys.some((key) => key in obj);
};

// Detect array of nested form entries (objects that may have __meta and field keys)
function isNestedFormEntries(
  value: unknown
): value is Record<string, unknown>[] {
  if (!Array.isArray(value) || value.length === 0) return false;
  return value.every(
    (item) =>
      typeof item === 'object' &&
      item !== null &&
      !Array.isArray(item) &&
      !(item instanceof File)
  );
}

function isKeyPersonsData(value: unknown): value is {
  selectedIds: string[];
  entries: Record<string, Record<string, unknown>>;
  [PERSON_LABELS_KEY]?: Record<string, string>;
} {
  if (typeof value !== 'object' || value === null || Array.isArray(value))
    return false;
  const obj = value as Record<string, unknown>;
  return (
    Array.isArray(obj.selectedIds) &&
    typeof obj.entries === 'object' &&
    obj.entries !== null
  );
}

const CUSTOM_COMPONENT_TYPES = new Set([
  'NestedForm',
  'KeyPersons',
  'IndirectTaxRegistration',
]);

// Value formatter for nested form entries
const formatNestedValue = (
  value: unknown,
  fieldId?: string,
  componentMap?: Record<string, unknown>
): string => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  const baseFieldId = fieldId?.includes('.')
    ? fieldId.split('.').pop()
    : fieldId;

  if (baseFieldId && componentMap) {
    const component = componentMap[baseFieldId] as
      | {
          component?: string;
          options?: Array<{ value: string; label: string }>;
          props?: Record<string, unknown>;
        }
      | undefined;

    if (
      component?.component === 'NumberInput' ||
      (component?.component === 'AutoDetectField' && typeof value === 'number')
    ) {
      const formatted = formatNumberForDisplay(value, component.props);
      if (formatted !== null) return formatted;
    }

    if (
      component &&
      component.options &&
      Array.isArray(component.options) &&
      component.options.length > 0
    ) {
      if (component.component === 'CheckboxGroup') {
        if (
          typeof value === 'object' &&
          value !== null &&
          !Array.isArray(value)
        ) {
          const obj = value as Record<string, boolean>;
          const selectedLabels: string[] = [];

          Object.entries(obj).forEach(([optionValue, isSelected]) => {
            if (isSelected === true) {
              const normalizedOptionValue =
                optionValue != null ? String(optionValue).trim() : '';

              if (normalizedOptionValue !== '') {
                const option = component.options!.find((opt) => {
                  if (opt.value == null) {
                    return normalizedOptionValue === '';
                  }
                  return String(opt.value).trim() === normalizedOptionValue;
                });

                if (option) {
                  const displayLabel =
                    option.label?.trim() || String(option.value);
                  if (displayLabel) {
                    selectedLabels.push(displayLabel);
                  }
                } else {
                  selectedLabels.push(normalizedOptionValue);
                }
              }
            }
          });

          return selectedLabels.length > 0 ? selectedLabels.join(', ') : '—';
        }
      }

      if (
        component.component === 'RadioGroup' ||
        component.component === 'Select'
      ) {
        const option = component.options.find(
          (opt) => String(opt.value) === String(value)
        );
        if (option && option.label) {
          return option.label;
        }
      }
    }
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'object') {
    if (value instanceof File) {
      return value.name;
    }
    if (Array.isArray(value)) {
      if (isMultiFileUpload(value)) {
        const fileNames = value
          .map((file) => {
            const fileName = (file as Record<string, unknown>).fileName;
            return fileName != null && fileName !== ''
              ? String(fileName)
              : null;
          })
          .filter((name): name is string => name !== null);
        return fileNames.length > 0 ? fileNames.join(', ') : '—';
      }
      return value.length > 0
        ? value
            .map((v) => formatNestedValue(v, undefined, componentMap))
            .join(', ')
        : '—';
    }
    const obj = value as Record<string, unknown>;

    // Ownership node: summarise as an owner list, not a raw object dump.
    if (isOwnershipValue(obj)) {
      const node = baseFieldId ? componentMap?.[baseFieldId] : undefined;
      return formatOwnershipValue(
        value,
        readPartnershipStructure(
          (node as { props?: Record<string, unknown> } | undefined)?.props
        )
      );
    }

    if (isFileUpload(obj)) {
      const fileName = obj.fileName;
      if (fileName === null || fileName === undefined || fileName === '') {
        return '—';
      }
      return String(fileName);
    }

    if (isAddressInput(obj)) {
      const addressParts: string[] = [];
      const fields = [
        'addressLine1',
        'addressLine2',
        'postalCode',
        'city',
        'state',
      ];
      for (const field of fields) {
        const fieldValue = obj[field];
        if (
          fieldValue !== null &&
          fieldValue !== undefined &&
          fieldValue !== ''
        ) {
          addressParts.push(String(fieldValue));
        }
      }
      return addressParts.length > 0 ? addressParts.join(', ') : '—';
    }

    // Nested form entry with __meta
    const meta = obj.__meta as
      | { labels?: Record<string, string>; formName?: string }
      | undefined;
    if (meta !== undefined && typeof meta === 'object') {
      const dataEntries = Object.entries(obj).filter(
        ([key]) => key !== '__meta'
      );
      if (dataEntries.length > 0) {
        const labelMap = meta.labels ?? {};
        const parts = dataEntries
          .filter(([, v]) => v !== null && v !== undefined && v !== '')
          .map(([k, v]) => {
            const label = labelMap[k] || formatFieldId(k);
            return `${label}: ${formatNestedValue(v, k, componentMap)}`;
          });
        return parts.length > 0 ? parts.join(', ') : '—';
      }
    }

    // Default object formatting
    const parts = Object.entries(obj)
      .filter(([, v]) => v !== null && v !== undefined && v !== '')
      .map(
        ([k, v]) =>
          `${formatFieldId(k)}: ${formatNestedValue(v, undefined, componentMap)}`
      );
    return parts.length > 0 ? parts.join(', ') : '—';
  }
  return String(value);
};

// =============================================================================
// DynamicFieldsSummarySection
// Renders DynamicFields data as a grid of fields with proper labels.
// Uses static field definitions from the component props or fetches from Content Library.
// =============================================================================

interface DynamicFieldDefinition {
  key: string;
  label: string;
  type?: string;
  options?: Array<{ value: string; label: string }>;
  displayOrder?: number;
  groupKey?: string;
}

interface DynamicFieldsSummarySectionProps {
  fieldId: string;
  data: Record<string, unknown>;
  component: {
    label?: string;
    props?: {
      fieldsSource?: {
        type: 'static' | 'field' | 'context' | 'content-library';
        fields?: DynamicFieldDefinition[];
        contentId?: string;
      };
    };
    dataSource?: {
      type?: string;
      contentLibrary?: {
        contentId?: string;
      };
    };
  };
  sectionLabel: string;
}

function DynamicFieldsSummarySection({
  fieldId,
  data,
  component,
  sectionLabel,
}: DynamicFieldsSummarySectionProps) {
  // Get field definitions from static config if available
  const staticFieldDefs = useMemo((): DynamicFieldDefinition[] => {
    const fieldsSource = component.props?.fieldsSource;
    if (fieldsSource?.type === 'static' && fieldsSource.fields) {
      const fields = [...fieldsSource.fields];

      // Sort by displayOrder
      fields.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

      return fields;
    }
    return [];
  }, [component]);

  // Build a map of field key -> field definition for quick lookup
  const fieldDefMap = useMemo((): Record<string, DynamicFieldDefinition> => {
    const map: Record<string, DynamicFieldDefinition> = {};
    for (const def of staticFieldDefs) {
      map[def.key] = def;
    }
    return map;
  }, [staticFieldDefs]);

  // Get data entries, sorted by field definition order if available
  const sortedEntries = useMemo((): Array<[string, unknown]> => {
    const entries = Object.entries(data).filter(
      ([, v]) => v !== null && v !== undefined && v !== ''
    );

    if (staticFieldDefs.length > 0) {
      // Sort by field definition order
      const orderMap = new Map<string, number>();
      staticFieldDefs.forEach((def, idx) => {
        orderMap.set(def.key, idx);
      });

      entries.sort(([a], [b]) => {
        const orderA = orderMap.get(a) ?? 999;
        const orderB = orderMap.get(b) ?? 999;
        return orderA - orderB;
      });
    }

    return entries;
  }, [data, staticFieldDefs]);

  if (sortedEntries.length === 0) {
    return null;
  }

  // Build data points for GridView
  const dataPoints: TGridViewDataPoint[] = sortedEntries.map(([key, value]) => {
    const fieldDef = fieldDefMap[key];
    const label = fieldDef?.label || formatFieldId(key);
    const formattedValue = formatDynamicFieldValue(value, fieldDef);

    return {
      heading: label,
      value: formattedValue,
    };
  });

  return (
    <div className="space-y-2">
      {sectionLabel && (
        <h5 className="text-text-secondary txt-small-medium">{sectionLabel}</h5>
      )}
      <GridView dataPoints={dataPoints} disableRowClick />
    </div>
  );
}

// =============================================================================
// NestedFormSummarySection
// Fetches the linked template's label map and renders the entries with proper
// field labels. Works for both new entries (with __meta.labels) and old ones.
// =============================================================================

type TemplateModuleGroup = {
  moduleId: string;
  moduleName: string;
  fieldIds: string[];
  labelMap: Record<string, string>;
  saveKey?: string;
  repeatable?: boolean;
};

interface NestedFormSummarySectionProps {
  fieldId: string;
  entries: Record<string, unknown>[];
  keyId: string;
  sectionLabel: string;
  onDownload?: (fileValue: unknown) => void;
  onPreview?: (fileValue: unknown) => void;
}

function NestedFormSummarySection({
  fieldId,
  entries,
  keyId,
  sectionLabel,
  onDownload,
  onPreview,
}: NestedFormSummarySectionProps) {
  const { key } = useFormTemplateKey(keyId || null);
  const { data: templateData } = useFormsTemplate(key?.liveFormId ?? null);
  const template = templateData?.formsTemplate;

  // Build module-grouped structure from the linked template's DSL
  const templateModules = useMemo((): TemplateModuleGroup[] => {
    if (!template?.dsl) return [];
    const dsl = template.dsl;
    const groups: TemplateModuleGroup[] = [];

    (dsl.steps ?? []).forEach((step: any) => {
      (step.modules ?? []).forEach((mod: any) => {
        const fieldIds: string[] = [];
        const labelMap: Record<string, string> = {};

        (mod.components ?? []).forEach((comp: any) => {
          if (comp.fieldId) {
            fieldIds.push(comp.fieldId);
            if (comp.label) {
              labelMap[comp.fieldId] = comp.label;
            }
          }
        });

        if (fieldIds.length > 0) {
          groups.push({
            moduleId: mod.id,
            moduleName: mod.name || mod.id,
            fieldIds,
            labelMap,
            saveKey: mod.saveKey,
            repeatable: Boolean(mod.repeatable?.enabled),
          });
        }
      });
    });

    return groups;
  }, [template]);

  // Map fieldId -> keyId for inner NestedForm components (for recursive rendering)
  const fieldIdToNestedKeyId = useMemo((): Record<string, string> => {
    const map: Record<string, string> = {};
    if (!template?.dsl) return map;
    const dsl = template.dsl;
    (dsl.steps ?? []).forEach((step: any) => {
      (step.modules ?? []).forEach((mod: any) => {
        (mod.components ?? []).forEach((comp: any) => {
          if (
            comp.fieldId &&
            comp.component === 'NestedForm' &&
            (comp.props as { keyId?: string } | undefined)?.keyId
          ) {
            map[comp.fieldId] = (comp.props as { keyId: string }).keyId;
          }
        });
      });
    });
    return map;
  }, [template]);

  // Flat label map for fallback (from __meta or template)
  const flatLabelMap = useMemo((): Record<string, string> => {
    const map: Record<string, string> = {};
    for (const mod of templateModules) {
      Object.assign(map, mod.labelMap);
    }
    return map;
  }, [templateModules]);

  const getMetaLabelMap = (
    entry: Record<string, unknown>
  ): Record<string, string> => {
    const meta = entry.__meta as
      | { labels?: Record<string, string> }
      | undefined;
    return meta?.labels ?? {};
  };

  const formName =
    template?.name ||
    (entries[0]?.__meta as { formName?: string } | undefined)?.formName ||
    sectionLabel;

  const hasModules = templateModules.length > 0;

  return (
    <div className="space-y-3">
      {entries.map((entryObj, entryIndex) => {
        const metaLabelMap = getMetaLabelMap(entryObj);
        const entryHeading =
          entries.length > 1 ? `${formName} ${entryIndex + 1}` : null;

        if (hasModules) {
          return (
            <div key={`${fieldId}-${entryIndex}`} className="space-y-3">
              {templateModules.map((mod) => {
                // Repeatable modules store instances as an array under their
                // saveKey (module id when absent), not per fieldId.
                if (mod.repeatable) {
                  const instanceKey = mod.saveKey || mod.moduleId;
                  const raw = entryObj[instanceKey];
                  const instances = Array.isArray(raw) ? raw : [];

                  const blocks = instances
                    .map((instance: unknown, instanceIndex: number) => {
                      const obj = (instance ?? {}) as Record<string, unknown>;
                      const points = mod.fieldIds
                        .map((fId): TGridViewDataPoint | null => {
                          const val = obj[fId];
                          if (val === null || val === undefined || val === '')
                            return null;
                          return {
                            heading:
                              mod.labelMap[fId] ||
                              metaLabelMap[fId] ||
                              formatFieldId(fId),
                            value: formatNestedValue(
                              val,
                              `${instanceKey}.${instanceIndex}.${fId}`
                            ),
                          };
                        })
                        .filter((dp): dp is TGridViewDataPoint => dp !== null);
                      return points.length > 0
                        ? { instanceIndex, points }
                        : null;
                    })
                    .filter(
                      (
                        b
                      ): b is {
                        instanceIndex: number;
                        points: TGridViewDataPoint[];
                      } => b !== null
                    );

                  if (blocks.length === 0) return null;

                  return (
                    <div key={mod.moduleId} className="space-y-2">
                      <h5 className="text-text-secondary txt-small-medium">
                        {mod.moduleName}
                      </h5>
                      {blocks.map((block) => (
                        <div
                          key={`${mod.moduleId}-${block.instanceIndex}`}
                          className="space-y-1"
                        >
                          {blocks.length > 1 && (
                            <p className="text-text-tertiary txt-small-regular">
                              {`${mod.moduleName} ${block.instanceIndex + 1}`}
                            </p>
                          )}
                          <GridView
                            dataPoints={block.points}
                            disableRowClick
                          />
                        </div>
                      ))}
                    </div>
                  );
                }

                const modEntries = mod.fieldIds
                  .map((fId): [string, unknown] | null => {
                    const val = entryObj[fId];
                    if (val === null || val === undefined || val === '')
                      return null;
                    return [fId, val];
                  })
                  .filter((e): e is [string, unknown] => e !== null);

                if (modEntries.length === 0) return null;

                // Split into regular fields and nested form fields
                const regularEntries: [string, unknown][] = [];
                const nestedFormFields: {
                  fId: string;
                  entries: Record<string, unknown>[];
                  nestedKeyId: string;
                  label: string;
                }[] = [];

                for (const [k, v] of modEntries) {
                  const nestedKeyId = fieldIdToNestedKeyId[k];
                  const entriesArray = Array.isArray(v)
                    ? v
                    : v != null &&
                        typeof v === 'object' &&
                        !Array.isArray(v) &&
                        !(v instanceof File)
                      ? [v as Record<string, unknown>]
                      : [];
                  if (
                    nestedKeyId &&
                    entriesArray.length > 0 &&
                    isNestedFormEntries(entriesArray)
                  ) {
                    nestedFormFields.push({
                      fId: k,
                      entries: entriesArray,
                      nestedKeyId,
                      label:
                        mod.labelMap[k] || metaLabelMap[k] || formatFieldId(k),
                    });
                  } else {
                    regularEntries.push([k, v]);
                  }
                }

                const dataPoints: TGridViewDataPoint[] = regularEntries.map(
                  ([k, v]) => {
                    const heading =
                      mod.labelMap[k] || metaLabelMap[k] || formatFieldId(k);
                    const dp: TGridViewDataPoint = {
                      heading,
                      value: formatNestedValue(v, k),
                    };
                    if (
                      (isFileUpload(v) || isMultiFileUpload(v)) &&
                      (onDownload || onPreview)
                    ) {
                      dp.actions = [];
                      if (onPreview)
                        dp.actions.push({
                          icon: Icons.Eye,
                          tooltip: 'Preview',
                          onClick: () => onPreview(v),
                        });
                      if (onDownload)
                        dp.actions.push({
                          icon: Icons.Download01,
                          tooltip: 'Download',
                          onClick: () => onDownload(v),
                        });
                    }
                    return dp;
                  }
                );

                return (
                  <div key={mod.moduleId} className="space-y-2">
                    <h5 className="text-text-secondary txt-small-medium">
                      {mod.moduleName}
                    </h5>
                    {dataPoints.length > 0 && (
                      <GridView
                        dataPoints={dataPoints}
                        disableRowClick
                      />
                    )}
                    {nestedFormFields.map((nested) => (
                      <NestedFormSummarySection
                        key={`${fieldId}-${entryIndex}-${nested.fId}`}
                        fieldId={nested.fId}
                        entries={nested.entries}
                        keyId={nested.nestedKeyId}
                        sectionLabel={nested.label}
                        onDownload={onDownload}
                        onPreview={onPreview}
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          );
        }

        // Fallback: flat rendering when no template structure is available
        const dataEntries = Object.entries(entryObj).filter(
          ([k, v]) =>
            k !== '__meta' && v !== null && v !== undefined && v !== ''
        );

        if (dataEntries.length === 0) return null;

        // Split into regular fields and nested form fields
        const regularEntries: [string, unknown][] = [];
        const nestedFields: {
          fId: string;
          entries: Record<string, unknown>[];
          nestedKeyId: string;
          label: string;
        }[] = [];

        for (const [k, v] of dataEntries) {
          const nestedKeyId = fieldIdToNestedKeyId[k];
          const entriesArray = Array.isArray(v)
            ? v
            : v != null &&
                typeof v === 'object' &&
                !Array.isArray(v) &&
                !(v instanceof File)
              ? [v as Record<string, unknown>]
              : [];
          if (
            nestedKeyId &&
            entriesArray.length > 0 &&
            isNestedFormEntries(entriesArray)
          ) {
            nestedFields.push({
              fId: k,
              entries: entriesArray,
              nestedKeyId,
              label: flatLabelMap[k] || metaLabelMap[k] || formatFieldId(k),
            });
          } else {
            regularEntries.push([k, v]);
          }
        }

        const dataPoints: TGridViewDataPoint[] = regularEntries.map(
          ([k, v]) => {
            const heading =
              flatLabelMap[k] || metaLabelMap[k] || formatFieldId(k);
            const dp: TGridViewDataPoint = {
              heading,
              value: formatNestedValue(v, k),
            };
            if (
              (isFileUpload(v) || isMultiFileUpload(v)) &&
              (onDownload || onPreview)
            ) {
              dp.actions = [];
              if (onPreview)
                dp.actions.push({
                  icon: Icons.Eye,
                  tooltip: 'Preview',
                  onClick: () => onPreview(v),
                });
              if (onDownload)
                dp.actions.push({
                  icon: Icons.Download01,
                  tooltip: 'Download',
                  onClick: () => onDownload(v),
                });
            }
            return dp;
          }
        );

        return (
          <div key={`${fieldId}-${entryIndex}`} className="space-y-2">
            {entryHeading && (
              <h5 className="text-text-secondary txt-small-medium">
                {entryHeading}
              </h5>
            )}
            {dataPoints.length > 0 && (
              <GridView dataPoints={dataPoints} disableRowClick />
            )}
            {nestedFields.map((nested) => (
              <NestedFormSummarySection
                key={`${fieldId}-${entryIndex}-${nested.fId}`}
                fieldId={nested.fId}
                entries={nested.entries}
                keyId={nested.nestedKeyId}
                sectionLabel={nested.label}
                onDownload={onDownload}
                onPreview={onPreview}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}

interface IndirectTaxRegistrationSummarySectionProps {
  value: Record<string, unknown>;
  sectionLabel: string;
  corporationId?: number;
}

function IndirectTaxRegistrationSummarySection({
  value,
  sectionLabel,
  corporationId,
}: IndirectTaxRegistrationSummarySectionProps) {
  const createdRegistrationId =
    typeof value.createdTaxRegistrationId === 'string'
      ? value.createdTaxRegistrationId
      : undefined;

  const { data: registration } = useIndirectTaxRegistration({
    registrationId: createdRegistrationId ?? '',
    corporationId: corporationId ?? 0,
  });

  const storedTaxTypes = Array.isArray(value.taxTypes)
    ? value.taxTypes
    : undefined;
  const storedStartDate =
    typeof value.effectiveStartDate === 'string'
      ? value.effectiveStartDate
      : null;

  const rows: Array<[string, string | undefined | null]> = registration
    ? [
        ['Registration', registration.registrationName],
        [
          'Jurisdiction',
          IndirectTaxFilingsHelper.formatJurisdictionName(
            registration.jurisdictionName
          ),
        ],
        [
          'Tax types',
          IndirectTaxRegistrationsHelper.formatTaxTypes(registration.taxTypes),
        ],
        [
          'Filing frequency',
          IndirectTaxRegistrationsHelper.formatFrequencies(
            registration.frequencies
          ),
        ],
        [
          'Effective start date',
          formatIsoDateShort(registration.effectiveStartDate),
        ],
        ['Tax registration number', registration.taxRegistrationNumber],
        [
          'Status',
          registration.registrationStatus
            ? enumKeyToDisplayName(registration.registrationStatus)
            : undefined,
        ],
      ]
    : [
        [
          'Tax types',
          IndirectTaxRegistrationsHelper.formatTaxTypes(storedTaxTypes),
        ],
        [
          'Filing frequency',
          typeof value.frequency === 'string' && value.frequency
            ? IndirectTaxRegistrationsHelper.formatFrequency(value.frequency)
            : undefined,
        ],
        ['Effective start date', formatIsoDateShort(storedStartDate)],
      ];

  const crossBorderRows: Array<[string, string | undefined]> = [
    [
      'Supplies or acquires goods across EU borders',
      typeof value.intraCommunityTrade === 'boolean'
        ? value.intraCommunityTrade
          ? 'Yes'
          : 'No'
        : undefined,
    ],
    [
      'Imports or exports goods outside the EU/UK',
      typeof value.importsOrExportsGoods === 'boolean'
        ? value.importsOrExportsGoods
          ? 'Yes'
          : 'No'
        : undefined,
    ],
  ];

  const ukActivity =
    typeof value.ukActivity === 'object' && value.ukActivity !== null
      ? (value.ukActivity as UKActivity)
      : undefined;

  const ukOptionLabel = (
    options: ReadonlyArray<{ value: string; label: string }>,
    optionValue: string | undefined
  ) =>
    optionValue
      ? (options.find((option) => option.value === optionValue)?.label ??
        optionValue)
      : '';

  const ukOptionLabels = (
    options: ReadonlyArray<{ value: string; label: string }>,
    optionValues: string[] | undefined
  ) =>
    optionValues?.length
      ? optionValues
          .map((optionValue) => ukOptionLabel(options, optionValue))
          .join(', ')
      : '';

  const ukYesNo = (flag: boolean | undefined) =>
    typeof flag === 'boolean' ? (flag ? 'Yes' : 'No') : '';

  const ukProviderLabel = (provider: {
    name: string;
    address: IAddress;
  }) =>
    [
      provider.name,
      [
        provider.address?.addressLine1,
        provider.address?.city,
        provider.address?.postalCode,
        provider.address?.country,
      ]
        .filter(Boolean)
        .join(', '),
    ]
      .filter(Boolean)
      .join(' - ');

  const ukRows: Array<[string, string | undefined]> = ukActivity
    ? [
        [
          'UK VAT registration reasons',
          ukOptionLabels(
            IndirectTaxRegistrationHelper.UK_VAT_REGISTRATION_REASON_OPTIONS,
            ukActivity.ukVATRegistrationReasons
          ),
        ],
        ['UK activity description', ukActivity.ukActivityDescription],
        [
          'Goods stored or shipped from UK',
          ukYesNo(ukActivity.goodsStoredOrShippedFromUK),
        ],
        [
          'UK goods providers',
          ukActivity.ukGoodProviders?.length
            ? ukActivity.ukGoodProviders.map(ukProviderLabel).join('; ')
            : undefined,
        ],
        [
          'Has UK exempt supplies',
          ukOptionLabel(
            IndirectTaxRegistrationHelper.UK_EXEMPT_SUPPLIES_ANSWER_OPTIONS,
            ukActivity.hasUKExemptSupplies
          ),
        ],
        [
          'UK presence types',
          ukOptionLabels(
            IndirectTaxRegistrationHelper.UK_PRESENCE_TYPE_OPTIONS,
            ukActivity.ukPresenceTypes
          ),
        ],
        ['UK presence details', ukActivity.ukPresenceDetails],
        [
          'Directors involved in other UK business',
          ukYesNo(ukActivity.directorsInvolvedInOtherUKBusiness),
        ],
        ['Other UK businesses details', ukActivity.otherUKBusinessesDetails],
      ]
    : [];

  const dataPoints: TGridViewDataPoint[] = [...rows, ...crossBorderRows, ...ukRows]
    .filter((entry): entry is [string, string] => Boolean(entry[1]))
    .map(([heading, entryValue]) => ({ heading, value: entryValue }));

  if (dataPoints.length === 0) return null;

  return (
    <GridView dataPoints={dataPoints} title={sectionLabel} disableRowClick />
  );
}

export function Review({
  submissionId,
  className,
  corporationId,
  showCopyButton = false,
  pageTitle,
}: ReviewProps) {
  const {
    data: formSubmissionData,
    isLoading: isLoadingFormSubmission,
    error,
  } = useFormSubmission(submissionId);

  const {
    data: formTemplateData,
    isLoading: isLoadingFormTemplate,
    error: errorFormTemplate,
  } = useFormsTemplate(formSubmissionData?.formSubmission?.formTemplateId);

  const formResponse = formSubmissionData?.formSubmission?.formResponse as
    | Record<string, unknown>
    | undefined;

  const { parsedSteps, formatValue } = useSubmissionParser(
    formTemplateData?.formsTemplate?.dsl,
    formSubmissionData?.formSubmission?.formResponse
  );

  const { setNotification } = useNotification();

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewFileUrl, setPreviewFileUrl] = useState<string | null>(null);
  const [previewFileName, setPreviewFileName] = useState<string | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  const handleCopy = (value: string) => {
    const success = copyToClipboard(value);
    if (success) {
      setNotification({
        show: true,
        title: 'Success',
        message: 'Copied to clipboard',
        valid: true,
      });
    } else {
      setNotification({
        show: true,
        title: 'Failed to copy',
        message: 'Failed to copy to clipboard',
        valid: false,
      });
    }
  };

  const handleDownload = async (fileValue: unknown) => {
    if (!isFileUpload(fileValue)) {
      return;
    }

    const fileObj = fileValue as Record<string, unknown>;
    const fileName = fileObj.fileName as string;
    const fileUrl = fileObj.fileUrl as string;
    const fileId = (fileObj.fileId ?? fileObj.id) as number | undefined;

    try {
      if (fileId && typeof fileId === 'number') {
        await downloadFileFromFileId(fileId, fileName);
        setNotification({
          show: true,
          title: 'Success',
          message: 'File download started',
          valid: true,
        });
      } else if (fileUrl && typeof fileUrl === 'string') {
        const signedUrl = await getSignedUrlForHostedFileUrl(
          fileUrl,
          corporationId
        );
        downloadFileFromLink(signedUrl, fileName);
        setNotification({
          show: true,
          title: 'Success',
          message: 'File download started',
          valid: true,
        });
      } else {
        setNotification({
          show: true,
          title: 'Failed to download',
          message: 'File information is missing',
          valid: false,
        });
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      setNotification({
        show: true,
        title: 'Failed to download',
        message: 'An error occurred while downloading the file',
        valid: false,
      });
    }
  };

  const handlePreview = async (fileValue: unknown) => {
    if (!isFileUpload(fileValue)) {
      return;
    }

    const fileObj = fileValue as Record<string, unknown>;
    const fileName = fileObj.fileName as string;
    const fileUrl = fileObj.fileUrl as string;
    const fileId = (fileObj.fileId ?? fileObj.id) as number | undefined;

    setIsLoadingPreview(true);
    setPreviewFileName(fileName || 'Preview file');
    setIsPreviewOpen(true);

    try {
      let signedUrl: string | null = null;

      if (fileId && typeof fileId === 'number') {
        signedUrl = await getSignedUrlByFileId(fileId, undefined);
      } else if (fileUrl && typeof fileUrl === 'string') {
        signedUrl = await getSignedUrlForHostedFileUrl(fileUrl, corporationId);
      }

      if (signedUrl) {
        setPreviewFileUrl(signedUrl);
      } else {
        setNotification({
          show: true,
          title: 'Failed to preview',
          message: 'File information is missing or could not be loaded',
          valid: false,
        });
        setIsPreviewOpen(false);
      }
    } catch (error) {
      console.error('Error previewing file:', error);
      setNotification({
        show: true,
        title: 'Failed to preview',
        message: 'An error occurred while loading the file preview',
        valid: false,
      });
      setIsPreviewOpen(false);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleClosePreview = () => {
    setIsPreviewOpen(false);
    setPreviewFileUrl(null);
    setPreviewFileName(null);
  };

  if (
    isLoadingFormSubmission ||
    isLoadingFormTemplate ||
    !formSubmissionData ||
    !formTemplateData
  ) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingIndicator />
      </div>
    );
  }

  if (error || errorFormTemplate) {
    return (
      <div className="text-center py-12">
        <Icons.AlertCircle className="w-12 h-12 text-text-error mx-auto mb-4" />
        <p className="txt-medium-medium text-text-primary mb-2">
          Failed to load form
        </p>
        <p className="txt-small-regular text-text-tertiary">
          The form template could not be found or there was an error loading it.
        </p>
      </div>
    );
  }

  // Transform a single module into GridView data points
  const getDataPointsForModule = (
    parsedModule: (typeof parsedSteps)[number]['modules'][number]
  ): TGridViewDataPoint[] => {
    const dataPoints: TGridViewDataPoint[] = [];

    for (const field of parsedModule.fields) {
      // Skip NestedForm and DynamicFields - they're rendered separately
      if (field.component?.component === 'NestedForm') continue;
      if (field.component?.component === 'DynamicFields') continue;
      if (
        field.component?.component &&
        CUSTOM_COMPONENT_TYPES.has(field.component.component)
      )
        continue;

      // Ownership node: rendered as its own full-width section below.
      if (OwnershipSummaryHelper.isOwnershipField(field)) continue;

      const formattedValue = formatValue(field.value, field.fieldId);
      const isFile = isFileUpload(field.value);
      const isMultiFile = isMultiFileUpload(field.value);

      if (isMultiFile) {
        const files = field.value as Array<Record<string, unknown>>;
        // Render multi-file as ReactNode with individual file actions
        const multiFileValue = (
          <div className="flex flex-col w-full divide-y divide-dashed divide-border-primary">
            {files.map((file, index) => {
              const fileName = (file.fileName as string) || `File ${index + 1}`;
              return (
                <div
                  key={index}
                  className="flex items-center gap-1 py-lg first:pt-0 last:pb-0"
                >
                  <TruncatedText className="txt-small-regular text-text-primary flex-1">
                    {fileName}
                  </TruncatedText>
                  <DataPointButton
                    onClick={() => handlePreview(file)}
                    tooltip="Preview"
                    icon={Icons.Eye}
                  />
                  <DataPointButton
                    onClick={() => handleDownload(file)}
                    tooltip="Download"
                    icon={Icons.Download01}
                  />
                </div>
              );
            })}
          </div>
        );

        dataPoints.push({
          heading: field.label,
          value: multiFileValue,
          // No row-level onCopy - each file has its own copy button
        });
      } else if (isFile) {
        // Single file with preview/download actions
        dataPoints.push({
          heading: field.label,
          value: formattedValue,
          actions: [
            {
              icon: Icons.Eye,
              tooltip: 'Preview',
              onClick: () => handlePreview(field.value),
            },
            {
              icon: Icons.Download01,
              tooltip: 'Download',
              onClick: () => handleDownload(field.value),
            },
          ],
        });
      } else {
        // Regular field
        dataPoints.push({
          heading: field.label,
          value: formattedValue,
          onCopy: showCopyButton ? () => handleCopy(formattedValue) : undefined,
        });
      }
    }

    return dataPoints;
  };

  return (
    <div
      className={cn(
        'flex flex-col gap-4xl w-full max-w-[768px] mx-auto',
        className || 'p-6'
      )}
    >
      {parsedSteps.map((parsedStep) => (
        <div key={parsedStep.step.id} className="flex flex-col gap-xl">
          {!isSameTitle(parsedStep.step.title, pageTitle) && (
            <h3 className="txt-large-bold text-text-primary">
              {parsedStep.step.title}
            </h3>
          )}
          <div className="flex flex-col gap-2xl">
            {parsedStep.modules.map((parsedModule) => {
              const dataPoints = getDataPointsForModule(parsedModule);
              const nestedFormFields = parsedModule.fields.filter(
                (f) => f.component?.component === 'NestedForm'
              );
              const dynamicFieldsComponents = parsedModule.fields.filter(
                (f) =>
                  f.component?.component === 'DynamicFields' &&
                  f.value != null &&
                  typeof f.value === 'object' &&
                  !Array.isArray(f.value)
              );
              const keyPersonsFields = parsedModule.fields.filter(
                (f) => f.component?.component === 'KeyPersons'
              );
              const indirectTaxFields = parsedModule.fields.filter(
                (f) => f.component?.component === 'IndirectTaxRegistration'
              );
              const ownershipFields = parsedModule.fields.filter(
                OwnershipSummaryHelper.isOwnershipField
              );
              const hasCustomFields =
                dynamicFieldsComponents.length > 0 ||
                ownershipFields.length > 0 ||
                nestedFormFields.some(
                  (f) =>
                    (Array.isArray(f.value)
                      ? f.value
                      : f.value != null &&
                          typeof f.value === 'object' &&
                          !Array.isArray(f.value)
                        ? [f.value]
                        : []
                    ).length > 0
                ) ||
                keyPersonsFields.some((f) => {
                  if (!isKeyPersonsData(f.value)) return false;
                  const { selectedIds, entries } = f.value;
                  const labels = f.value[PERSON_LABELS_KEY];
                  return selectedIds.some(
                    (pid) => entries[pid] != null || Boolean(labels?.[pid])
                  );
                }) ||
                indirectTaxFields.some(
                  (f) =>
                    f.value != null &&
                    typeof f.value === 'object' &&
                    !Array.isArray(f.value)
                );
              if (dataPoints.length === 0 && !hasCustomFields) return null;
              return (
                <div
                  key={parsedModule.module.id}
                  className="flex flex-col gap-md"
                >
                  {parsedModule.module.name && dataPoints.length === 0 && (
                    <h4 className="txt-medium-bold text-text-primary">
                      {parsedModule.module.name}
                    </h4>
                  )}
                  {dataPoints.length > 0 && (
                    <GridView
                      dataPoints={dataPoints}
                      title={parsedModule.module.name}
                      description={parsedModule.module.description}
                      disableRowClick
                    />
                  )}
                  {dynamicFieldsComponents.map((field) => (
                    <DynamicFieldsSummarySection
                      key={field.fieldId}
                      fieldId={field.fieldId}
                      data={field.value as Record<string, unknown>}
                      component={
                        field.component as DynamicFieldsSummarySectionProps['component']
                      }
                      sectionLabel={field.label}
                    />
                  ))}
                  {nestedFormFields.map((field) => {
                    const keyId =
                      ((field.component?.props as Record<string, unknown>)
                        ?.keyId as string) || '';
                    const entryArray: Record<string, unknown>[] = Array.isArray(
                      field.value
                    )
                      ? (field.value as Record<string, unknown>[])
                      : field.value != null &&
                          typeof field.value === 'object' &&
                          !Array.isArray(field.value)
                        ? [field.value as Record<string, unknown>]
                        : [];
                    if (entryArray.length === 0) return null;
                    return (
                      <NestedFormSummarySection
                        key={field.fieldId}
                        fieldId={field.fieldId}
                        entries={entryArray}
                        keyId={keyId}
                        sectionLabel={field.label}
                        onDownload={handleDownload}
                        onPreview={handlePreview}
                      />
                    );
                  })}
                  {keyPersonsFields.map((field) => {
                    if (!isKeyPersonsData(field.value)) return null;
                    const { selectedIds, entries: personEntries } = field.value;
                    if (selectedIds.length === 0) return null;

                    const keyId =
                      ((field.component?.props as Record<string, unknown>)
                        ?.keyId as string) || '';

                    const entryArray = selectedIds
                      .map((pid) => personEntries[pid])
                      .filter((e): e is Record<string, unknown> => e != null);

                    if (entryArray.length > 0) {
                      return (
                        <NestedFormSummarySection
                          key={field.fieldId}
                          fieldId={field.fieldId}
                          entries={entryArray}
                          keyId={keyId}
                          sectionLabel={field.label}
                          onDownload={handleDownload}
                          onPreview={handlePreview}
                        />
                      );
                    }

                    const personLabels = field.value[PERSON_LABELS_KEY];
                    if (typeof personLabels !== 'object' || personLabels === null)
                      return null;
                    const names = selectedIds
                      .map((pid) => personLabels[pid])
                      .filter((name): name is string => Boolean(name));
                    if (names.length === 0) return null;

                    return (
                      <GridView
                        key={field.fieldId}
                        title={field.label}
                        dataPoints={[{ heading: 'Name', value: names.join(', ') }]}
                        disableRowClick
                      />
                    );
                  })}
                  {ownershipFields.map((field) => (
                    <OwnershipSummaryView
                      key={field.fieldId}
                      title={field.label}
                      value={field.value}
                      partnershipStructure={readPartnershipStructure(
                        field.component?.props
                      )}
                      entityDetailsKeyId={readEntityDetailsKeyId(
                        field.component?.props
                      )}
                      formData={formResponse}
                    />
                  ))}
                  {indirectTaxFields.map((field) => {
                    if (
                      field.value == null ||
                      typeof field.value !== 'object' ||
                      Array.isArray(field.value)
                    )
                      return null;
                    return (
                      <IndirectTaxRegistrationSummarySection
                        key={field.fieldId}
                        value={field.value as Record<string, unknown>}
                        sectionLabel={field.label}
                        corporationId={corporationId}
                      />
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      ))}
      <Dialog open={isPreviewOpen} onOpenChange={handleClosePreview}>
        <DialogContent className="flex flex-col gap-4 max-w-[800px]">
          <DialogHeader className="flex flex-col items-start gap-4">
            <div className="flex flex-col gap-1">
              <DialogTitle>{previewFileName || 'Preview file'}</DialogTitle>
            </div>
          </DialogHeader>

          <div className="h-[500px] overflow-y-auto p-6 bg-bg-secondary">
            {isLoadingPreview ? (
              <div className="flex justify-center items-center h-full">
                <LoadingIndicator />
              </div>
            ) : (
              previewFileUrl && <DocViewerComponent uri={previewFileUrl} />
            )}
          </div>

          <DialogFooter>
            <div className="flex justify-end w-full gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleClosePreview}
              >
                Close
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
